import { useState } from "react";
import { Download, Trash2, FileEdit, Clock, CheckCircle2, XCircle, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useDsar, DSAR_KIND_LABELS, type DsarKind } from "@/lib/compliance/useDsar";

const KIND_ICONS: Record<DsarKind, React.ElementType> = {
  export: Download,
  delete: Trash2,
  rectify: FileEdit,
};

const STATUS_ICONS = {
  pending: Clock,
  in_progress: Clock,
  completed: CheckCircle2,
  rejected: XCircle,
};

const fmt = (iso: string) => new Date(iso).toLocaleDateString();

const DsarPanel = () => {
  const { requests, loading, submit } = useDsar();
  const [open, setOpen] = useState<DsarKind | null>(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubmit = async () => {
    if (!open) return;
    if (open === "delete" && !confirm(
      "Are you sure? Deletion is permanent and removes your profile, photos, matches, and message history within 30 days."
    )) return;
    setBusy(true);
    const { error } = await submit(open, notes.trim() || undefined);
    setBusy(false);
    if (error) {
      toast.error(error);
      return;
    }
    setOpen(null);
    setNotes("");
    toast.success("Request received. We'll respond within 30 days.");
  };

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Under GDPR, CCPA, and similar laws, you have the right to access, correct,
        and delete your personal data. We respond within 30 days.
      </p>

      <div className="grid gap-2 sm:grid-cols-3">
        {(Object.keys(DSAR_KIND_LABELS) as DsarKind[]).map((kind) => {
          const Icon = KIND_ICONS[kind];
          return (
            <Dialog
              key={kind}
              open={open === kind}
              onOpenChange={(o) => {
                if (!o) {
                  setOpen(null);
                  setNotes("");
                } else setOpen(kind);
              }}
            >
              <DialogTrigger asChild>
                <Button variant="outline" className="justify-start">
                  <Icon className="h-4 w-4 mr-2" />
                  {DSAR_KIND_LABELS[kind]}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{DSAR_KIND_LABELS[kind]}</DialogTitle>
                  <DialogDescription>
                    {kind === "export" && "We'll prepare a copy of your data and email you a download link within 30 days."}
                    {kind === "delete" && "We'll delete your profile, content, and personal data within 30 days. Some records are retained for legal/compliance reasons (see Privacy Policy)."}
                    {kind === "rectify" && "Tell us what data is wrong and what it should be. We'll review and update it."}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-2">
                  <Textarea
                    placeholder={kind === "rectify" ? "Describe what to correct…" : "Optional notes…"}
                    rows={4}
                    maxLength={1000}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setOpen(null)}>Cancel</Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={busy}
                    variant={kind === "delete" ? "destructive" : "default"}
                  >
                    {busy ? "Submitting…" : "Submit request"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          );
        })}
      </div>

      {requests.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Your requests
          </p>
          {requests.map((r) => {
            const StatusIcon = STATUS_ICONS[r.status];
            const canDownload =
              r.kind === "export" && (r.status === "pending" || r.status === "completed");
            const handleDownload = async () => {
              const { data, error } = await supabase.functions.invoke<{ bundle: unknown }>(
                "dsar-export",
                { body: { dsar_id: r.id } },
              );
              if (error || !data?.bundle) {
                toast.error(error?.message ?? "Export failed.");
                return;
              }
              const blob = new Blob([JSON.stringify(data.bundle, null, 2)], { type: "application/json" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `wavlynq-data-${new Date().toISOString().slice(0, 10)}.json`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
              toast.success("Export downloaded.");
            };
            return (
              <div key={r.id} className="rounded-lg border border-border bg-card px-4 py-2.5 flex items-center justify-between gap-3">
                <div className="text-sm">
                  <span className="font-medium">{DSAR_KIND_LABELS[r.kind]}</span>
                  <span className="ml-2 text-xs text-muted-foreground">{fmt(r.created_at)}</span>
                </div>
                <div className="flex items-center gap-3">
                  {canDownload && (
                    <Button size="sm" variant="ghost" onClick={handleDownload}>
                      <FileDown className="h-3.5 w-3.5 mr-1" />
                      Download
                    </Button>
                  )}
                  <span className={`inline-flex items-center gap-1.5 text-xs ${
                    r.status === "completed" ? "text-success"
                      : r.status === "rejected" ? "text-destructive"
                      : "text-muted-foreground"
                  }`}>
                    <StatusIcon className="h-3.5 w-3.5" />
                    <span className="capitalize">{r.status.replace("_", " ")}</span>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DsarPanel;
